import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styles from './StrategyForm.module.css';
import Button from '../../common/Button/Button';
import { toast } from 'react-toastify';
import api from '../../../services/api';
import { strategyEngineService } from '../../../services/strategyEngineService';
import { watchlistService } from '../../../services/watchlistService';
import { strategyGroupsService } from '../../../services/strategyGroupsService';
import {
  SIZING_QTY_MIN, SIZING_QTY_MAX,
  SIZING_CAPITAL_MIN, SIZING_CAPITAL_MAX,
  TSL_PCT_MIN, TSL_PCT_MAX,
  TARGET_PCT_MIN, TARGET_PCT_MAX,
  TARGET_PRICE_MIN, TARGET_PRICE_MAX,
  BROKERAGE_PCT_MIN, BROKERAGE_PCT_MAX,
} from './constants';
import StepBasicInfo from './StepBasicInfo';
import StepStrategyLogic from './StepStrategyLogic';
import StepReview from './StepReview';

// Strategy "Mode" is multi-select. The API may return it as a JSONB array, a
// JSON string, or a legacy bare string (e.g. 'Notification', or the old
// 'Alert'). Normalize any of those into an array of mode values, mapping the
// retired 'Alert' value to 'Signal', and never returning an empty list.
const normalizeModes = (value) => {
  let arr;
  if (Array.isArray(value)) {
    arr = value;
  } else if (typeof value === 'string') {
    const s = value.trim();
    if (s.startsWith('[')) {
      try { arr = JSON.parse(s); } catch { arr = [s]; }
    } else {
      arr = s ? [s] : [];
    }
  } else {
    arr = [];
  }
  if (!Array.isArray(arr)) arr = [];
  const mapped = arr
    .filter((m) => typeof m === 'string' && m.trim())
    .map((m) => (m === 'Alert' ? 'Signal' : m));
  const deduped = [...new Set(mapped)];
  return deduped.length ? deduped : ['Signal'];
};

const StrategyForm = ({ strategy, onSubmit, onCancel, onFormDataChange, loading }) => {
  const [step, setStep] = useState(1);
  const [symbolOptions, setSymbolOptions] = useState([]);
  const [symbolSearch, setSymbolSearch] = useState('');
  const [showSymbolDropdown, setShowSymbolDropdown] = useState(false);
  const symbolDropdownRef = useRef(null);
  const [strategyConfig, setStrategyConfig] = useState(null);

  // ── Watchlist import (Step 1) ───────────────────────────
  // Lets the logged-in user pull symbols from their mobile-app
  // watchlists into the strategy. This is a CONVENIENCE SOURCE only:
  // picked stocks flow into the SAME `formData.symbols` array as
  // manual picking (via addSymbol — dedupe already enforced),
  // so nothing downstream changes. Identity is cookie-derived on the
  // backend (no user_id sent). If the fetch fails or the user has no
  // watchlists, `watchlists` stays empty and the whole block renders
  // nothing — the form is byte-for-byte the same as before, manual
  // picking fully intact. A watchlist problem can never block the form.
  const [watchlists, setWatchlists] = useState([]);
  const [selectedWatchlistId, setSelectedWatchlistId] = useState('');
  const [watchlistStocks, setWatchlistStocks] = useState([]);
  const [watchlistStocksLoading, setWatchlistStocksLoading] = useState(false);
  const [watchlistsLoading, setWatchlistsLoading] = useState(true);
  const [watchlistsError, setWatchlistsError] = useState('');

  // ── Symbol source tracking (parallel Map) ───────────────
  // Tracks WHERE each symbol was added from (index / watchlist / search).
  // Key: symbol string → Value: { source, sourceLabel }.
  // Purely for display grouping — formData.symbols stays a flat string[].
  const [symbolSources, setSymbolSources] = useState(new Map());

  // ── Group / Index import (Step 1) ───────────────────────
  // Same convenience-source pattern as the watchlist, but the source is
  // a GLOBAL group (NIFTY, BANKEX, a sector…) rather than a per-user
  // list. A group is identified by the composite (type, name) — some
  // names exist under two types. Picks feed the SAME `formData.symbols`
  // array. Fetch failure / no groups → the fields still render with
  // an inline error/empty message. `groups` is a flat [{ type, name }]
  // list; selection is tracked by its index so the (type, name) pair
  // stays intact without delimiter parsing.
  const [groups, setGroups] = useState([]);
  const [selectedGroupIdx, setSelectedGroupIdx] = useState('');
  const [groupStocks, setGroupStocks] = useState([]);
  const [groupStocksLoading, setGroupStocksLoading] = useState(false);
  const [groupStockSearch, setGroupStockSearch] = useState('');
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [groupsError, setGroupsError] = useState('');
  // Custom dropdown (replaces a native <select>): native can't cap the
  // open-popup height and renders <optgroup> labels in bold-italic.
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const [groupSearch, setGroupSearch] = useState('');
  // Auto-flip + height-cap: the popup must stay inside the modal's
  // SCROLLABLE container (the modal body), which is what clips it — NOT
  // the viewport (there's unusable dark space below the modal). So we
  // measure against that container, cap the menu to the real available
  // space so it can never be clipped, and flip upward only when below
  // is genuinely tighter than above.
  const [groupDropUp, setGroupDropUp] = useState(false);
  const [groupMenuMaxH, setGroupMenuMaxH] = useState(240);
  const groupDropdownRef = useRef(null);

  const openGroupDropdown = useCallback(() => {
    const el = groupDropdownRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      // Nearest scrollable/clipping ancestor = the boundary that clips.
      let clip = el.parentElement;
      while (clip) {
        const oy = window.getComputedStyle(clip).overflowY;
        if (oy === 'auto' || oy === 'scroll' || oy === 'hidden') break;
        clip = clip.parentElement;
      }
      const bound = clip
        ? clip.getBoundingClientRect()
        : { top: 0, bottom: window.innerHeight };
      const gap = 8;
      const spaceBelow = bound.bottom - rect.bottom - gap;
      const spaceAbove = rect.top - bound.top - gap;
      const up = spaceAbove > spaceBelow && spaceBelow < 220;
      const avail = up ? spaceAbove : spaceBelow;
      setGroupDropUp(up);
      // Cap to available space (never clips); clamp to a usable range.
      setGroupMenuMaxH(Math.max(120, Math.min(240, Math.floor(avail))));
    }
    setShowGroupDropdown(true);
    setGroupSearch('');
  }, []);
  const [formData, setFormData] = useState({
    name: '',
  
    symbols: [],
    action: 'BUY',
    alert_or_trade: ['Signal'],
    conditions: [],
    exit_conditions: [],
    // Position sizing — optional on the strategy. If the user
    // leaves it disabled, backtest P&L stays "Not configured"
    // (no fake numbers). When enabled, `type` picks the model
    // and `value` is the number of shares OR the rupee amount.
    position_sizing: null,
    // Trailing stop loss — exit RULE (sibling of exit_conditions,
    // not of position_sizing). When configured, the engine closes
    // the position when price falls `value` percent below the
    // running peak (long) / rises that percent above the running
    // trough (short). Evaluated BEFORE exit_conditions on every
    // candle, in both backtest AND live signal generation, so the
    // behaviour is identical wherever the strategy runs.
    trailing_stop_loss: null,
    // Target (take-profit) — exit RULE. When configured, the engine
    // closes the position when price reaches the target (above entry
    // for long, below for short). Checked after stop loss, before
    // exit_conditions, in both backtest AND live. percent = % from
    // entry; price = absolute level.
    target: null,
    // Brokerage & taxes — cost CONFIG (not a rule). Per-side
    // percentages applied to trade value so backtest P&L comes out
    // net of costs. When null, backtests report gross P&L.
    brokerage_config: null,
    // Notifications — when enabled, every signal this strategy fires
    // (entry/exit/SL/TSL) is pushed to the selected notification_query
    // groups. Disabled + empty groups ⇒ identical to a pre-feature
    // strategy (nothing is ever enqueued).
    notification_enabled: false,
    notification_group_ids: [],
    // Trade Type id (FK to trade_type) for the "Create Recommendation" mode.
    // null when that mode isn't selected.
    reco_trade_type_id: null,
    // Audience for the "Create Recommendation" draft notification. Empty array
    // = notify the strategy creator only (default).
    reco_notification_group_ids: [],
  });

  // ── Notification groups (audience options) ──────────────
  // The list of notification_query groups an admin can target. Sourced
  // from the main backend (same endpoint the Notification Query screen
  // uses). A fetch failure leaves the list empty and shows an inline
  // "none available" message — it never blocks the form.
  const [notificationGroups, setNotificationGroups] = useState([]);

  // Trade Type options for the "Create Recommendation" mode. Sourced from the
  // same /tradetype endpoint the Trade Recommendation form uses, so the list
  // always matches the business-approved types (Intraday / Weekly / ...). A
  // fetch failure leaves it empty and never blocks the form.
  const [tradeTypes, setTradeTypes] = useState([]);

  // Fetch internal tokens for symbol autocomplete (same API as TradeRecommendationForm)
  useEffect(() => {
    const fetchTokens = async () => {
      try {
        const res = await api.get('/internaltoken/list');
        if (res.status && res.data) {
          const options = res.data.map(t => ({
            value: t.symbol,
            label: `${t.name} (${t.symbol}) [${t.token}] - ${t.exch_seg || ''}`,
          }));
          setSymbolOptions(options);
        }
      } catch (error) {
        console.error('Error fetching tokens for symbol search:', error);
      }
    };
    fetchTokens();
  }, []);

  // Fetch strategy config (indicator options) once on mount
  useEffect(() => {
    strategyEngineService.getStrategyOptions()
      .then(setStrategyConfig)
      .catch(err => console.error('Failed to load strategy config:', err));
  }, []);

  // Fetch the available notification groups once on mount. Optional —
  // any failure leaves the list empty (the section shows a "none
  // available" hint) and never blocks the form.
  useEffect(() => {
    let cancelled = false;
    api.get('/notification-query')
      .then(res => {
        if (cancelled) return;
        const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        // Exclude PARAMETERIZED queries (those containing a $1/$2 placeholder).
        // Those need a runtime bind value (e.g. a news-feed id) supplied by a
        // specific flow — the strategy broadcaster runs the stored SQL with no
        // parameters, so a parameterized group can never resolve recipients
        // here. Hiding them prevents an admin from picking an audience that
        // would silently notify nobody.
        const usable = list.filter(g => !/\$\d/.test(String(g.queries || '')));
        setNotificationGroups(usable);
      })
      .catch(() => { if (!cancelled) setNotificationGroups([]); });
    return () => { cancelled = true; };
  }, []);

  // Fetch Trade Type options once on mount (same endpoint as the Trade
  // Recommendation form). Optional — failure leaves the list empty.
  useEffect(() => {
    let cancelled = false;
    api.get('/tradetype')
      .then(res => {
        if (cancelled) return;
        const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        const options = list
          .filter(t => t && t.tradeTypeId != null)
          .map(t => ({ value: Number(t.tradeTypeId), label: t.tradeTypeName }));
        setTradeTypes(options);
      })
      .catch(() => { if (!cancelled) setTradeTypes([]); });
    return () => { cancelled = true; };
  }, []);

  // Fetch the logged-in user's watchlists once on mount. Optional
  // feature — any failure (endpoint absent, network, not logged in)
  // is swallowed so manual symbol picking is never affected. The
  // `cancelled` guard avoids a setState after unmount.
  useEffect(() => {
    let cancelled = false;
    setWatchlistsLoading(true);
    setWatchlistsError('');
    watchlistService.getMyWatchlists()
      .then(res => {
        if (cancelled) return;
        const lists = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        setWatchlists(lists);
      })
      .catch(() => {
        if (!cancelled) setWatchlistsError('Failed to load watchlists');
      })
      .finally(() => {
        if (!cancelled) setWatchlistsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Fetch the global group/index list once on mount. Optional — any
  // failure shows an inline error; manual picking is never affected.
  useEffect(() => {
    let cancelled = false;
    setGroupsLoading(true);
    setGroupsError('');
    strategyGroupsService.getGroups()
      .then(res => {
        if (cancelled) return;
        const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        setGroups(list);
      })
      .catch(() => {
        if (!cancelled) setGroupsError('Failed to load groups');
      })
      .finally(() => {
        if (!cancelled) setGroupsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // (Removed: single-symbol search-sync effect — multi-select
  //  shows selected symbols as chips, so the search input stays
  //  empty and is used purely for finding the *next* symbol to add.)

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (symbolDropdownRef.current && !symbolDropdownRef.current.contains(e.target)) {
        setShowSymbolDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Click outside to close the custom group dropdown (separate ref so
  // it doesn't touch the symbol-dropdown handler above).
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (groupDropdownRef.current && !groupDropdownRef.current.contains(e.target)) {
        setShowGroupDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // useMemo — filters synchronously during render, no delay.
  // Matches from START of name or symbol (not anywhere in label).
  // Multi-select: also excludes any symbols already selected, so
  // the dropdown only ever offers things you can actually add.
  const filteredSymbols = useMemo(() => {
    if (!symbolSearch.trim()) return [];
    const query = symbolSearch.toLowerCase();
    const selectedSet = new Set(formData.symbols);
    return symbolOptions.filter(o => {
      if (selectedSet.has(o.value)) return false;
      const name = o.label.split('(')[0].trim().toLowerCase();  // "Reliance Industries"
      const symbol = o.value.toLowerCase();                      // "RELIANCE"
      return name.startsWith(query) || symbol.startsWith(query);
    });
  }, [symbolSearch, symbolOptions, formData.symbols]);

  // Set of symbols the engine can actually trade — the same master
  // (/internaltoken/list) that powers manual autocomplete. Watchlist
  // stocks NOT in this set are shown greyed/disabled so a user can't
  // add an untradeable symbol (which would save fine but silently
  // never fire). Keeps watchlist picks consistent with manual ones.
  const tradableSymbolSet = useMemo(
    () => new Set(symbolOptions.map(o => o.value)),
    [symbolOptions]
  );

  // Groups bucketed by exchange (NSE / BSE) for the dropdown sections.
  // Each entry keeps its index into the flat `groups` array so selecting
  // one recovers the group's canonical group_token.
  const groupedByExchange = useMemo(() => {
    const buckets = new Map();
    groups.forEach((g, idx) => {
      const exchange = g.exchange || 'Other';
      if (!buckets.has(exchange)) buckets.set(exchange, []);
      buckets.get(exchange).push({ idx, name: g.name });
    });
    return [...buckets.entries()].map(([exchange, items]) => ({ exchange, items }));
  }, [groups]);

  // Sections filtered by the dropdown search box. Empty sections are
  // dropped so the list stays tight.
  const filteredGroupedByExchange = useMemo(() => {
    const q = groupSearch.trim().toLowerCase();
    if (!q) return groupedByExchange;
    return groupedByExchange
      .map(({ exchange, items }) => ({
        exchange,
        items: items.filter(it => it.name.toLowerCase().includes(q) || exchange.toLowerCase().includes(q)),
      }))
      .filter(g => g.items.length > 0);
  }, [groupedByExchange, groupSearch]);

  // Display name for the currently-selected group (custom dropdown
  // has no native value rendering).
  const selectedGroupName = (selectedGroupIdx !== '' && groups[Number(selectedGroupIdx)])
    ? groups[Number(selectedGroupIdx)].name
    : '';

  // Constituents filtered by the search box — groups can hold up to
  // ~500 stocks, so a filter keeps the picker usable.
  const filteredGroupStocks = useMemo(() => {
    const q = groupStockSearch.trim().toUpperCase();
    if (!q) return groupStocks;
    return groupStocks.filter(s => (s?.symbol || '').toUpperCase().includes(q));
  }, [groupStocks, groupStockSearch]);

  // Pre-fill for edit mode. The backend now returns a `symbols`
  // array (junction-driven). For older responses that only have
  // a `symbol` string we wrap it into a one-element array so the
  // multi-select still loads cleanly.
  useEffect(() => {
    if (strategy) {
      const symbols = Array.isArray(strategy.symbols) && strategy.symbols.length > 0
        ? [...strategy.symbols]
        : (strategy.symbol ? [strategy.symbol] : []);

      // Populate symbolSources from the backend's symbol_states
      // (which now includes source / source_label). Existing
      // strategies that pre-date the feature have source='search'.
      const srcMap = new Map();
      const states = Array.isArray(strategy.symbol_states) ? strategy.symbol_states : [];
      for (const st of states) {
        if (st?.symbol) {
          srcMap.set(st.symbol, {
            source: st.source || 'search',
            sourceLabel: st.source_label || null,
          });
        }
      }
      // Any symbol in the flat list without a state entry defaults to 'search'.
      for (const sym of symbols) {
        if (!srcMap.has(sym)) {
          srcMap.set(sym, { source: 'search', sourceLabel: null });
        }
      }
      setSymbolSources(srcMap);
      // Defensive: backend stores position_sizing as JSONB; pg
      // typically returns it parsed, but some response paths may
      // hand back a stringified blob. Cope with both.
      let positionSizing = strategy.position_sizing ?? null;
      if (typeof positionSizing === 'string') {
        try { positionSizing = JSON.parse(positionSizing); }
        catch { positionSizing = null; }
      }
      let trailingStopLoss = strategy.trailing_stop_loss ?? null;
      if (typeof trailingStopLoss === 'string') {
        try { trailingStopLoss = JSON.parse(trailingStopLoss); }
        catch { trailingStopLoss = null; }
      }
      if (trailingStopLoss) {
        if (trailingStopLoss.type === 'fixed') {
          const num = Number(trailingStopLoss.value);
          if (!Number.isFinite(num) || num < TSL_PCT_MIN || num > TSL_PCT_MAX) {
            trailingStopLoss = null;
          }
        } else if (trailingStopLoss.type === 'percent') {
          const num = Number(trailingStopLoss.value);
          if (!Number.isFinite(num) || num < TSL_PCT_MIN || num > TSL_PCT_MAX) {
            trailingStopLoss = null;
          }
        } else if (trailingStopLoss.type === 'stepped') {
          const i = Number(trailingStopLoss.initial_stop_pct);
          const h = Number(trailingStopLoss.hurdle_pct);
          const s = Number(trailingStopLoss.step_pct);
          if (!Number.isFinite(i) || !Number.isFinite(h) || !Number.isFinite(s)) {
            trailingStopLoss = null;
          }
        } else {
          trailingStopLoss = null;
        }
      }
      let target = strategy.target ?? null;
      if (typeof target === 'string') {
        try { target = JSON.parse(target); }
        catch { target = null; }
      }
      if (target) {
        if (target.type === 'percent') {
          const num = Number(target.value);
          if (!Number.isFinite(num) || num < TARGET_PCT_MIN || num > TARGET_PCT_MAX) {
            target = null;
          }
        } else if (target.type === 'price') {
          const num = Number(target.value);
          if (!Number.isFinite(num) || num <= 0) {
            target = null;
          }
        } else {
          target = null;
        }
      }
      let brokerageConfig = strategy.brokerage_config ?? null;
      if (typeof brokerageConfig === 'string') {
        try { brokerageConfig = JSON.parse(brokerageConfig); }
        catch { brokerageConfig = null; }
      }
      if (brokerageConfig) {
        const buy = Number(brokerageConfig.buy_pct);
        const sell = Number(brokerageConfig.sell_pct);
        if (
          brokerageConfig.type !== 'percent' ||
          !Number.isFinite(buy) || !Number.isFinite(sell) ||
          buy < BROKERAGE_PCT_MIN || buy > BROKERAGE_PCT_MAX ||
          sell < BROKERAGE_PCT_MIN || sell > BROKERAGE_PCT_MAX
        ) {
          brokerageConfig = null;
        } else {
          brokerageConfig = { type: 'percent', buy_pct: buy, sell_pct: sell };
        }
      }
      setFormData({
        name: strategy.name || '',
        symbols,
        timeframe: strategy.timeframe || '5m',
        action: strategy.action || 'BUY',
        // Mode is multi-select: normalize whatever shape the API returns
        // (JSONB array, JSON string, or legacy bare string) into an array.
        alert_or_trade: normalizeModes(strategy.alert_or_trade),
        conditions: strategy.conditions || [],
        exit_conditions: strategy.exit_conditions || [],
        position_sizing: positionSizing,
        trailing_stop_loss: trailingStopLoss,
        target,
        brokerage_config: brokerageConfig,
        notification_enabled: !!strategy.notification_enabled,
        notification_group_ids: Array.isArray(strategy.notification_group_ids)
          ? strategy.notification_group_ids.map(Number).filter(Number.isFinite)
          : [],
        reco_trade_type_id:
          strategy.reco_trade_type_id != null
            ? Number(strategy.reco_trade_type_id)
            : null,
        reco_notification_group_ids: Array.isArray(strategy.reco_notification_group_ids)
          ? strategy.reco_notification_group_ids.map(Number).filter(Number.isFinite)
          : [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategy]);

  // Report form data changes for progress bar
  useEffect(() => {
    if (onFormDataChange) {
      onFormDataChange(formData);
    }
  }, [formData, onFormDataChange]);

  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Multi-select helpers. Both are no-ops at the edges (duplicate
  // add, removing a missing entry) so the caller doesn't
  // need to pre-check. No count cap — strategies may hold any
  // number of symbols.
  const addSymbol = useCallback((value, source = 'search', sourceLabel = null) => {
    setFormData(prev => {
      if (!value) return prev;
      if (prev.symbols.includes(value)) return prev;
      return { ...prev, symbols: [...prev.symbols, value] };
    });
    // Track the source in the parallel Map (purely for display).
    setSymbolSources(prev => {
      if (prev.has(value)) return prev;
      const next = new Map(prev);
      next.set(value, { source, sourceLabel });
      return next;
    });
  }, []);

  // ── Watchlist handlers ──────────────────────────────────
  // Selecting a watchlist loads its stocks. A failed/empty load just
  // shows an empty state — never throws into the form.
  const handleSelectWatchlist = useCallback((wishlistId) => {
    setSelectedWatchlistId(wishlistId);
    setWatchlistStocks([]);
    if (!wishlistId) return;
    setWatchlistStocksLoading(true);
    const wl = watchlists.find(w => String(w.wishlist_id) === String(wishlistId));
    const wlName = wl?.wishlist_name || 'watchlist';
    watchlistService.getWatchlistStocks(wishlistId)
      .then(res => {
        const stocks = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        setWatchlistStocks(stocks);
        // Auto-select: picking a watchlist immediately adds EVERY
        // tradable, not-yet-added stock (no manual tapping). Untradable
        // skipped (would never fire); already-present skipped (dedupe).
        // Uses the freshly fetched `stocks` — not state — so no race.
        setFormData(prev => {
          const seen = new Set(prev.symbols);
          const next = [...prev.symbols];
          let added = 0, skippedUntradable = 0, skippedDup = 0;
          for (const s of stocks) {
            const sym = s?.script_id;
            if (!sym) continue;
            if (!tradableSymbolSet.has(sym)) { skippedUntradable++; continue; }
            if (seen.has(sym)) { skippedDup++; continue; }
            next.push(sym); seen.add(sym); added++;
          }
          if (added > 0) {
            toast.success(`${added} symbol${added > 1 ? 's' : ''} added from ${wlName}`, { toastId: 'wl-auto' });
          } else if (skippedUntradable > 0 && skippedDup === 0) {
            toast.info('No tradable stocks in this watchlist', { toastId: 'wl-auto' });
          } else if (skippedDup > 0) {
            toast.info(`All of ${wlName} is already added`, { toastId: 'wl-auto' });
          }
          // Track source for newly added symbols.
          if (added > 0) {
            const addedSymbols = next.slice(-added);
            setSymbolSources(prev => {
              const m = new Map(prev);
              for (const sym of addedSymbols) {
                if (!m.has(sym)) m.set(sym, { source: 'watchlist', sourceLabel: wlName });
              }
              return m;
            });
          }
          return added > 0 ? { ...prev, symbols: next } : prev;
        });
      })
      .catch(() => setWatchlistStocks([]))
      .finally(() => setWatchlistStocksLoading(false));
  }, [watchlists, tradableSymbolSet]);

  // ── Group handlers ──────────────────────────────────────
  // Mirror the watchlist handlers. Selecting a group resolves the
  // (type, name) pair from its index and loads constituents.
  const handleSelectGroup = useCallback((idxStr) => {
    setSelectedGroupIdx(idxStr);
    setGroupStocks([]);
    setGroupStockSearch('');
    if (idxStr === '') return;
    const g = groups[Number(idxStr)];
    if (!g) return;
    setGroupStocksLoading(true);
    strategyGroupsService.getGroupConstituents(g.group_token)
      .then(res => {
        const stocks = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        setGroupStocks(stocks);
        // Auto-select: picking a group immediately adds EVERY tradable,
        // not-yet-added constituent to the symbols list (no manual
        // tapping). Untradable ones are skipped (they'd never fire);
        // already-present ones are skipped (dedupe). Uses the freshly
        // fetched `stocks` — not state — so there's no stale-read race.
        setFormData(prev => {
          const seen = new Set(prev.symbols);
          const next = [...prev.symbols];
          let added = 0, skippedUntradable = 0, skippedDup = 0;
          for (const s of stocks) {
            const sym = s?.symbol;
            if (!sym) continue;
            if (!tradableSymbolSet.has(sym)) { skippedUntradable++; continue; }
            if (seen.has(sym)) { skippedDup++; continue; }
            next.push(sym); seen.add(sym); added++;
          }
          if (added > 0) {
            toast.success(`${added} symbol${added > 1 ? 's' : ''} added from ${g.name}`, { toastId: 'grp-auto' });
          } else if (skippedUntradable > 0 && skippedDup === 0) {
            toast.info('No tradable stocks in this group', { toastId: 'grp-auto' });
          } else if (skippedDup > 0) {
            toast.info(`All of ${g.name} is already added`, { toastId: 'grp-auto' });
          }
          // Track source for newly added symbols.
          if (added > 0) {
            const addedSymbols = next.slice(-added);
            setSymbolSources(prev => {
              const m = new Map(prev);
              for (const sym of addedSymbols) {
                if (!m.has(sym)) m.set(sym, { source: 'index', sourceLabel: g.name });
              }
              return m;
            });
          }
          return added > 0 ? { ...prev, symbols: next } : prev;
        });
      })
      .catch(() => setGroupStocks([]))
      .finally(() => setGroupStocksLoading(false));
  }, [groups, tradableSymbolSet]);

  // Position sizing helpers — keep state changes localised so
  // the JSX stays clean. Picking a type ALWAYS reseeds with a
  // type-appropriate default. We deliberately do NOT carry the
  // previous value across a type change: "100" as a share count
  // is not the same number as "100" as a rupee amount, and
  // silently preserving it would let a user save a wildly wrong
  // value (e.g. ₹50 capital when they meant 50 shares).
  const setSizingType = useCallback((type) => {
    setFormData(prev => {
      if (!type) {
        return { ...prev, position_sizing: null };
      }
      // No carry-over. Type swap = fresh default. User retypes
      // if they want something other than the default.
      const defaultValue = type === 'fixed_quantity' ? 100 : 50000;
      return {
        ...prev,
        position_sizing: { type, value: defaultValue },
      };
    });
  }, []);

  const setSizingValue = useCallback((rawValue) => {
    setFormData(prev => {
      if (!prev.position_sizing) return prev;
      // Keep the raw string in state while typing so the user
      // can clear / edit freely. Coerce to number at submit time
      // (Joi will reject anything non-numeric).
      return {
        ...prev,
        position_sizing: { ...prev.position_sizing, value: rawValue },
      };
    });
  }, []);

  const setTslType = useCallback((type) => {
    setFormData(prev => {
      if (!type) {
        return { ...prev, trailing_stop_loss: null };
      }
      if (prev.trailing_stop_loss?.type === type) {
        return prev;
      }
      if (type === 'fixed') {
        return {
          ...prev,
          trailing_stop_loss: { type: 'fixed', value: '' },
        };
      }
      if (type === 'percent') {
        return {
          ...prev,
          trailing_stop_loss: { type: 'percent', value: '' },
        };
      }
      if (type === 'stepped') {
        return {
          ...prev,
          trailing_stop_loss: { type: 'stepped', initial_stop_pct: '', hurdle_pct: '', step_pct: '' },
        };
      }
      return prev;
    });
  }, []);

  const setTslValue = useCallback((field, rawValue) => {
    setFormData(prev => {
      if (!prev.trailing_stop_loss) return prev;
      return {
        ...prev,
        trailing_stop_loss: { ...prev.trailing_stop_loss, [field]: rawValue },
      };
    });
  }, []);

  const setTargetType = useCallback((type) => {
    setFormData(prev => {
      if (!type) {
        return { ...prev, target: null };
      }
      if (prev.target?.type === type) {
        return prev;
      }
      // Both sub-types use a single { type, value } shape.
      return { ...prev, target: { type, value: '' } };
    });
  }, []);

  const setTargetValue = useCallback((rawValue) => {
    setFormData(prev => {
      if (!prev.target) return prev;
      return { ...prev, target: { ...prev.target, value: rawValue } };
    });
  }, []);

  // Brokerage helpers — same pattern as target: enabling seeds an
  // empty config (values stay strings while typing; coercion happens
  // at submit), disabling nulls it out. Re-enabling when already
  // configured is a no-op so a stray click can't wipe typed values.
  const setBrokerageEnabled = useCallback((enabled) => {
    setFormData(prev => {
      if (!enabled) {
        return { ...prev, brokerage_config: null };
      }
      if (prev.brokerage_config) {
        return prev;
      }
      return { ...prev, brokerage_config: { type: 'percent', buy_pct: '', sell_pct: '' } };
    });
  }, []);

  const setBrokerageValue = useCallback((field, rawValue) => {
    setFormData(prev => {
      if (!prev.brokerage_config) return prev;
      return {
        ...prev,
        brokerage_config: { ...prev.brokerage_config, [field]: rawValue },
      };
    });
  }, []);

  const removeSymbol = useCallback((value) => {
    setFormData(prev => ({
      ...prev,
      symbols: prev.symbols.filter(s => s !== value),
    }));
    setSymbolSources(prev => {
      if (!prev.has(value)) return prev;
      const next = new Map(prev);
      next.delete(value);
      return next;
    });
  }, []);

  // Remove ALL symbols belonging to a specific source category.
  // Used by the category header's × button.
  const removeCategory = useCallback((source, sourceLabel) => {
    setSymbolSources(prev => {
      const toRemove = new Set();
      for (const [sym, meta] of prev) {
        if (meta.source === source && meta.sourceLabel === sourceLabel) {
          toRemove.add(sym);
        }
      }
      if (toRemove.size === 0) return prev;
      const next = new Map(prev);
      for (const sym of toRemove) next.delete(sym);
      // Also remove from formData.symbols
      setFormData(fd => ({
        ...fd,
        symbols: fd.symbols.filter(s => !toRemove.has(s)),
      }));
      return next;
    });
  }, []);

  const handleSubmit = (activate = false) => {
    // Coerce position_sizing.value to a real number at submit
    // time (we kept it as a string in state while typing so the
    // user could edit freely). An invalid number → null so the
    // backend treats the strategy as not-configured rather than
    // rejecting the create.
    let positionSizing = formData.position_sizing;
    if (positionSizing && positionSizing.type) {
      const num = Number(positionSizing.value);
      positionSizing = Number.isFinite(num) && num > 0
        ? { type: positionSizing.type, value: num }
        : null;
    }
    let trailingStopLoss = formData.trailing_stop_loss;
    if (trailingStopLoss) {
      if (trailingStopLoss.type === 'fixed') {
        const num = Number(trailingStopLoss.value);
        trailingStopLoss = Number.isFinite(num) && num >= TSL_PCT_MIN && num <= TSL_PCT_MAX
          ? { type: 'fixed', value: num }
          : null;
      } else if (trailingStopLoss.type === 'percent') {
        const num = Number(trailingStopLoss.value);
        trailingStopLoss = Number.isFinite(num) && num >= TSL_PCT_MIN && num <= TSL_PCT_MAX
          ? { type: 'percent', value: num }
          : null;
      } else if (trailingStopLoss.type === 'stepped') {
        const i = Number(trailingStopLoss.initial_stop_pct);
        const h = Number(trailingStopLoss.hurdle_pct);
        const s = Number(trailingStopLoss.step_pct);
        trailingStopLoss = Number.isFinite(i) && Number.isFinite(h) && Number.isFinite(s)
          ? { type: 'stepped', initial_stop_pct: i, hurdle_pct: h, step_pct: s }
          : null;
      } else {
        trailingStopLoss = null;
      }
    }
    let target = formData.target;
    if (target) {
      if (target.type === 'percent') {
        const num = Number(target.value);
        target = Number.isFinite(num) && num >= TARGET_PCT_MIN && num <= TARGET_PCT_MAX
          ? { type: 'percent', value: num }
          : null;
      } else if (target.type === 'price') {
        const num = Number(target.value);
        target = Number.isFinite(num) && num >= TARGET_PRICE_MIN && num <= TARGET_PRICE_MAX
          ? { type: 'price', value: num }
          : null;
      } else {
        target = null;
      }
    }
    // Brokerage: coerce the string inputs to numbers; anything
    // incomplete or out of range collapses to null ("not configured")
    // so the backend never sees a half-filled config. Note Number('')
    // is 0 — which IS in the 0–5 range — so empty strings must be
    // rejected explicitly before coercion.
    let brokerageConfig = formData.brokerage_config;
    if (brokerageConfig) {
      const buyStr = String(brokerageConfig.buy_pct).trim();
      const sellStr = String(brokerageConfig.sell_pct).trim();
      const buy = Number(buyStr);
      const sell = Number(sellStr);
      const valid =
        buyStr !== '' && sellStr !== '' &&
        Number.isFinite(buy) && Number.isFinite(sell) &&
        buy >= BROKERAGE_PCT_MIN && buy <= BROKERAGE_PCT_MAX &&
        sell >= BROKERAGE_PCT_MIN && sell <= BROKERAGE_PCT_MAX;
      brokerageConfig = valid
        ? { type: 'percent', buy_pct: buy, sell_pct: sell }
        : null;
    }
    // Send symbols as objects with source metadata so the backend
    // can persist them in strategy_symbols.source / source_label.
    const symbolsWithSource = formData.symbols.map(sym => {
      const meta = symbolSources.get(sym);
      return {
        symbol: sym,
        source: meta?.source || 'search',
        source_label: meta?.sourceLabel || null,
      };
    });
    const payload = {
      ...formData,
      symbols: symbolsWithSource,
      position_sizing: positionSizing,
      trailing_stop_loss: trailingStopLoss,
      target,
      brokerage_config: brokerageConfig,
      is_active: activate,
    };
    onSubmit(payload);
  };

  // Returns true if the position_sizing block is either absent
  // (user chose "Not Configured") or fully valid. Used by Step 2's
  // Next button so an in-progress sizing can't be skipped past.
  const sizingValid = () => {
    const ps = formData.position_sizing;
    if (!ps) return true; // not configured = allowed
    if (!ps.type) return false;
    const num = Number(ps.value);
    if (!Number.isFinite(num) || num <= 0) return false;
    if (ps.type === 'fixed_quantity') {
      return Number.isInteger(num) && num >= SIZING_QTY_MIN && num <= SIZING_QTY_MAX;
    }
    return num >= SIZING_CAPITAL_MIN && num <= SIZING_CAPITAL_MAX;
  };

  const tslValid = () => {
    const tsl = formData.trailing_stop_loss;
    if (!tsl) return true; // not configured = allowed

    if (tsl.type === 'fixed') {
      const num = Number(tsl.value);
      if (!Number.isFinite(num)) return false;
      return num >= TSL_PCT_MIN && num <= TSL_PCT_MAX;
    }

    if (tsl.type === 'percent') {
      const num = Number(tsl.value);
      if (!Number.isFinite(num)) return false;
      return num >= TSL_PCT_MIN && num <= TSL_PCT_MAX;
    }
    
    if (tsl.type === 'stepped') {
      const i = Number(tsl.initial_stop_pct);
      const h = Number(tsl.hurdle_pct);
      const s = Number(tsl.step_pct);
      if (!Number.isFinite(i) || !Number.isFinite(h) || !Number.isFinite(s)) return false;
      return i > 0 && h >= 0 && s > 0;
    }

    return false;
  };

  const targetValid = () => {
    const t = formData.target;
    if (!t) return true; // not configured = allowed

    if (t.type === 'percent') {
      const num = Number(t.value);
      if (!Number.isFinite(num)) return false;
      return num >= TARGET_PCT_MIN && num <= TARGET_PCT_MAX;
    }

    if (t.type === 'price') {
      const num = Number(t.value);
      if (!Number.isFinite(num)) return false;
      return num >= TARGET_PRICE_MIN && num <= TARGET_PRICE_MAX;
    }

    return false;
  };

  const brokerageValid = () => {
    const b = formData.brokerage_config;
    if (!b) return true; // not configured = allowed

    // Empty strings must fail explicitly — Number('') is 0, which
    // would otherwise pass the 0–5 range check.
    const buyStr = String(b.buy_pct).trim();
    const sellStr = String(b.sell_pct).trim();
    if (buyStr === '' || sellStr === '') return false;

    const buy = Number(buyStr);
    const sell = Number(sellStr);
    if (!Number.isFinite(buy) || !Number.isFinite(sell)) return false;
    return buy >= BROKERAGE_PCT_MIN && buy <= BROKERAGE_PCT_MAX &&
      sell >= BROKERAGE_PCT_MIN && sell <= BROKERAGE_PCT_MAX;
  };

  // Groups are required ONLY when "Group Notification" is selected. "Notify me"
  // targets the creator and needs no audience; modes with no notification
  // target need nothing here.
  const notificationValid = () => {
    const modes = Array.isArray(formData.alert_or_trade) ? formData.alert_or_trade : [];
    if (!modes.includes('Notification')) return true;
    return Array.isArray(formData.notification_group_ids) && formData.notification_group_ids.length > 0;
  };

  // At least one mode must be selected (the backend rejects an empty mode set).
  const modeValid = () =>
    Array.isArray(formData.alert_or_trade) && formData.alert_or_trade.length > 0;

  // When "Create Recommendation" is selected, a Trade Type must be chosen.
  const recommendationValid = () => {
    const modes = Array.isArray(formData.alert_or_trade) ? formData.alert_or_trade : [];
    if (!modes.includes('CreateRecommendation')) return true;
    return formData.reco_trade_type_id != null;
  };

  const canGoNext = () => {
    if (step === 1) {
      return formData.name.trim() && formData.symbols.length > 0;
    }
    if (step === 2) {
      return formData.conditions.length > 0 &&
        formData.conditions.every(c => c.category && c.variable && c.operator && c.value && c.variableTimeframe) &&
        sizingValid() &&
        tslValid() &&
        targetValid() &&
        brokerageValid() &&
        modeValid() &&
        notificationValid() &&
        recommendationValid();
    }
    return true;
  };

  const renderConditionSummary = (condition) => {
    const varTf = condition.variableTimeframe || '1m';
    const value = condition.value;
    if (condition.valueType === 'IndicatorValue') {
      const valTf = condition.valueTimeframe || '1m';
      return `${condition.variable} [${varTf}] ${condition.operator} ${value} [${valTf}]`;
    }
    return `${condition.variable} [${varTf}] ${condition.operator} ${value}`;
  };

  return (
    <div className={styles.form}>
      {/* Step Indicator */}
      <div className={styles.stepIndicator}>
        {[1, 2, 3].map(s => (
          <div key={s} className={styles.stepRow}>
            <div className={`${styles.stepCircle} ${step >= s ? styles.stepActive : ''} ${step > s ? styles.stepCompleted : ''}`}>
              {step > s ? (
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>check</span>
              ) : s}
            </div>
            <span className={`${styles.stepLabel} ${step >= s ? styles.stepLabelActive : ''}`}>
              {s === 1 ? 'Basic Info' : s === 2 ? 'Conditions' : 'Review'}
            </span>
            {s < 3 && <div className={`${styles.stepLine} ${step > s ? styles.stepLineActive : ''}`} />}
          </div>
        ))}
      </div>

      {/* Step 1: Basic Info — Name + Symbol only */}
      {step === 1 && (
        <StepBasicInfo
          formData={formData}
          updateField={updateField}
          symbolSearch={symbolSearch}
          setSymbolSearch={setSymbolSearch}
          showSymbolDropdown={showSymbolDropdown}
          setShowSymbolDropdown={setShowSymbolDropdown}
          symbolDropdownRef={symbolDropdownRef}
          filteredSymbols={filteredSymbols}
          addSymbol={addSymbol}
          removeSymbol={removeSymbol}
          removeCategory={removeCategory}
          symbolSources={symbolSources}
          tradableSymbolSet={tradableSymbolSet}
          watchlistsLoading={watchlistsLoading}
          watchlistsError={watchlistsError}
          watchlists={watchlists}
          selectedWatchlistId={selectedWatchlistId}
          handleSelectWatchlist={handleSelectWatchlist}
          watchlistStocksLoading={watchlistStocksLoading}
          watchlistStocks={watchlistStocks}
          groupsLoading={groupsLoading}
          groupsError={groupsError}
          groups={groups}
          selectedGroupIdx={selectedGroupIdx}
          selectedGroupName={selectedGroupName}
          showGroupDropdown={showGroupDropdown}
          setShowGroupDropdown={setShowGroupDropdown}
          groupSearch={groupSearch}
          setGroupSearch={setGroupSearch}
          groupDropdownRef={groupDropdownRef}
          groupDropUp={groupDropUp}
          groupMenuMaxH={groupMenuMaxH}
          openGroupDropdown={openGroupDropdown}
          filteredGroupedByExchange={filteredGroupedByExchange}
          handleSelectGroup={handleSelectGroup}
          groupStocksLoading={groupStocksLoading}
          groupStocks={groupStocks}
          groupStockSearch={groupStockSearch}
          setGroupStockSearch={setGroupStockSearch}
          filteredGroupStocks={filteredGroupStocks}
        />
      )}

      {/* Step 2: Conditions + Mode + Action */}
      {step === 2 && (
        <StepStrategyLogic
          formData={formData}
          updateField={updateField}
          strategyConfig={strategyConfig}
          setSizingType={setSizingType}
          setSizingValue={setSizingValue}
          setTslType={setTslType}
          setTslValue={setTslValue}
          setTargetType={setTargetType}
          setTargetValue={setTargetValue}
          setBrokerageEnabled={setBrokerageEnabled}
          setBrokerageValue={setBrokerageValue}
          notificationGroups={notificationGroups}
          tradeTypes={tradeTypes}
        />
      )}

      {/* Step 3: Review */}
      {step === 3 && (
        <StepReview
          formData={formData}
          renderConditionSummary={renderConditionSummary}
        />
      )}

      {/* Navigation */}
      <div className={styles.navigation}>
        <div>
          {step > 1 && (
            <Button variant="outline" onClick={() => setStep(s => s - 1)} disabled={loading}>
              Previous
            </Button>
          )}
        </div>
        <div className={styles.navRight}>
          <Button variant="outline" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          {step < 3 ? (
            <Button variant="primary" onClick={() => setStep(s => s + 1)} disabled={!canGoNext()}>
              Next
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => handleSubmit(false)} loading={loading}>
                Save as Draft
              </Button>
              <Button variant="primary" onClick={() => handleSubmit(true)} loading={loading}>
                Save & Activate
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StrategyForm;
